/**
 * A camada de pedido dos módulos novos (05 e 06): `fetch`, a queda de erro por
 * situação e a frase que diz o que fazer.
 *
 * Os módulos 01, 02, 03, 08 e 12 têm cada um a sua cópia — pendência
 * registrada ("três api.ts", que já passou de três). Os módulos que nasceram
 * depois dela usam esta, em vez de fazer a cópia seguinte. Migrar os antigos
 * para cá é trabalho de quem integra, e não de uma tela nova.
 */
import { buscar } from './licenca-das-rotas';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';
import { licencaDaRecusa, perfilAPedir } from '@/lib/perfil-da-licenca';

export class SemSessao extends Error {
  constructor() {
    super('não há sessão aberta');
    this.name = 'SemSessao';
  }
}

export class ApiIndisponivel extends Error {
  constructor() {
    super('o sistema não respondeu');
    this.name = 'ApiIndisponivel';
  }
}

export class RecusaDaApi extends Error {
  readonly comoResolver: string;
  readonly status: number;
  /** O codigo da recusa, quando o servidor o manda (`ficha_alterada`, `situacao_mudou`). */
  readonly codigo: string | null;

  constructor(erro: string, comoResolver: string, status: number, codigo: string | null = null) {
    super(erro);
    this.name = 'RecusaDaApi';
    this.comoResolver = comoResolver;
    this.status = status;
    this.codigo = codigo;
  }
}

function quedaDoErro(status: number): { erro: string; comoResolver: string } {
  if (status === 403) {
    return {
      erro: 'você não tem permissão para isto',
      comoResolver: 'Peça a quem administra o sistema para liberar esta ação para o seu perfil.',
    };
  }
  if (status === 404) {
    return {
      erro: 'o servidor não conhece este endereço',
      comoResolver:
        'Recarregue a página. Se o sistema acabou de ser atualizado, o servidor ainda está com a versão anterior e precisa ser reiniciado.',
    };
  }
  if (status >= 500) {
    return {
      erro: 'o servidor falhou ao responder',
      comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
    };
  }
  return {
    erro: 'o pedido não foi aceito',
    comoResolver: 'Confira o que foi preenchido e tente outra vez; se continuar, avise o suporte com o horário.',
  };
}

export async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE_DA_API}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiIndisponivel();
  }

  /* O 401 DO PROPRIO LOGIN nao e sessao vencida: e a senha que nao confere.
     Tratado como `SemSessao`, a tela de entrada da plataforma escrevia "sua
     sessao expirou" para quem errou a senha — e escondia a frase do servidor,
     que diz qual senha usar. */
  if (resposta.status === 401 && !(init?.method === 'POST' && /\/sessao$/.test(caminho))) throw new SemSessao();

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => ({}))) as { erro?: string; comoResolver?: string; codigo?: unknown };
    const queda = quedaDoErro(resposta.status);
    throw new RecusaDaApi(
      corpo.erro ?? queda.erro,
      corpo.comoResolver ?? queda.comoResolver,
      resposta.status,
      typeof corpo.codigo === 'string' ? corpo.codigo : null,
    );
  }

  return (await resposta.json()) as T;
}

export const comCorpo = <T,>(metodo: 'POST' | 'PATCH' | 'DELETE', caminho: string, corpo?: unknown): Promise<T> =>
  pedir<T>(caminho, { method: metodo, ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }) });

/** A falha virada frase que diz o que fazer. */
export function explicarFalha(erro: unknown): { readonly erro: string; readonly comoResolver: string } {
  if (erro instanceof SemSessao) {
    return { erro: 'sua sessão expirou', comoResolver: 'Entre outra vez. O que você digitou continua na tela.' };
  }
  if (erro instanceof ApiIndisponivel) {
    return {
      erro: 'o sistema não respondeu',
      comoResolver: 'Confira a conexão e tente de novo; se continuar, avise o suporte com o horário.',
    };
  }
  /* A recusa desta camada, ou a das camadas antigas (o `ErroDaApi` da frota,
     dos cadastros): todas carregam `status` e `comoResolver`. Antes a das
     antigas caía no "avise o suporte" abaixo — foi o que a ficha do celular
     disse para quem não tem `prontuario.ler` (prova do marco, 07/10/2026). */
  const recusa = erro instanceof RecusaDaApi ? erro : comoRecusa(erro);
  if (recusa) {
    /* O 403 diz o PERFIL a pedir (p.34), e não só a licença: quem administra
       procura o perfil na governança. */
    const licenca = recusa.status === 403 ? licencaDaRecusa(recusa.comoResolver) : null;
    if (licenca) {
      const descricao = /“([^”]+)”/.exec(recusa.comoResolver)?.[1];
      return {
        erro: recusa.message,
        comoResolver: `Peça ao administrador da sua empresa o perfil ${perfilAPedir(licenca)}: é ele que tem a permissão ${descricao ? `“${descricao}”` : licenca}.`,
      };
    }
    return { erro: recusa.message, comoResolver: recusa.comoResolver };
  }
  return {
    erro: 'não foi possível falar com o servidor',
    comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
  };
}

/** A recusa de qualquer camada de pedido: um `Error` com `status` e `comoResolver`. */
function comoRecusa(erro: unknown): { readonly message: string; readonly status: number; readonly comoResolver: string } | null {
  if (!(erro instanceof Error)) return null;
  const { status, comoResolver } = erro as Error & { status?: unknown; comoResolver?: unknown };
  return typeof status === 'number' && typeof comoResolver === 'string' ? { message: erro.message, status, comoResolver } : null;
}
